<template>
	<div
		:class="[
			'fight-tile-label-popup',
			`fight-tile-label-popup--status-${popupStatus}`,
		]"
	>
		<div class="fight-tile-label-popup__header">
			<div class="fight-tile-label-popup__header__text">
				{{ headerText }}
			</div>
			<AppIconButton
				class="close-button"
				:type="IconTypes.CLOSE"
				@click="close"
			/>
		</div>
		<div class="fight-tile-label-popup__subheader">
			{{ subheaderText }}
		</div>
		<template v-if="popupStatus === 'active' || popupStatus === 'famine'">
			<div class="fight-tile-label-popup__subheader__line--narrower">
				<div
					class="fight-tile-label-popup__subheader__line--narrower__unit"
				>
					<div
						class="fight-tile-label-popup__subheader__line--narrower__unit__header"
					>
						{{ translate("ui.direct_attacks") }}
					</div>
					<ColorRectangle
						skin="yellow"
						:text="`${ssr.attackClickCount}/${ssr.maxAttackClickCount}`"
					/>
				</div>
				<div
					class="fight-tile-label-popup__subheader__line--narrower__unit"
				>
					<div
						class="fight-tile-label-popup__subheader__line--narrower__unit__header"
					>
						{{ translate("ui.supply_limit") }}
					</div>
					<ColorRectangle
						:skin="popupStatus === 'famine' ? 'red' : 'yellow'"
						:showArrow="popupStatus === 'famine'"
						:text="`${ssr.supplyLimitMax - ssr.supplyLimit}/${ssr.supplyLimitMax}`"
					/>
				</div>
				<div
					class="fight-tile-label-popup__subheader__line--narrower__unit"
				>
					<div
						class="fight-tile-label-popup__subheader__line--narrower__unit__header"
					>
						{{ translate("ui.enemy_units") }}
					</div>
					<ColorRectangle
						skin="yellow"
						:text="`${(ssr.enemiesTotal ?? 0) - (ssr.enemiesDefeated ?? 0)}`"
					/>
				</div>
			</div>
			<div
				v-if="popupStatus === 'famine'"
				class="fight-tile-label-popup__subheader__line--narrower"
			>
				<div class="fight-tile-label-popup__subheader__message">
					{{
						translate("ui.units_losing_health").replace(
							"{number}",
							formatPercent(
								FAMINE_HEALTH_LOSS_PER_TICK_PERCENTAGE,
								1,
							),
						)
					}}
				</div>
			</div>
			<div class="fight-tile-label-popup__subheader__header">
				<!-- {{ translate("ui.reward_for_victory") }} -->
				{{ translate("ui.your_attack_strength") }}
			</div>
			<div
				class="fight-tile-label-popup__subheader__line"
				v-for="report in reports"
				:key="report.text"
			>
				<div class="fight-tile-label-popup__subheader__line__text">
					{{ report.text }}
				</div>
				<div class="after-dots">
					<div class="fight-tile-label-popup__subheader__line__value">
						{{ report.value }}
					</div>
				</div>
			</div>
		</template>
		<HorizontalDivider />
		<div
			v-if="
				popupStatus === 'inactive' ||
				popupStatus === 'active' ||
				popupStatus === 'famine'
			"
			class="fight-tile-label-popup__content"
		>
			<div class="fight-tile-label-popup__content__header">
				{{ translate("ui.reward_for_victory") }}
			</div>
			<div class="fight-tile-label-popup__rewards">
				<div
					v-for="(amount, currency, index) in rewards"
					:key="index"
					class="fight-tile-label-popup__detail-reward"
				>
					<template v-if="amount && amount > 0">
						<Tooltip :key="currency" :placement="Placements.TOP">
							<template #element>
								<AppPrice
									:key="index"
									:amount="amount || 0"
									:currency="currency"
									:resourceCheck="false"
									:iconSize="16"
								/>
							</template>
							<template #tooltip>
								<span>
									{{
										translate(
											`resource.${resourceTypeToCurrencyType(
												currency,
											)}`,
										)
									}}
								</span>
							</template>
						</Tooltip></template
					>
				</div>
			</div>
			<template v-if="popupStatus === 'inactive'">
				<div class="fight-tile-label-popup__description">
					{{ translate("ui.available_battlefield_info") }}
				</div>
				<div class="fight-tile-label-popup__message">
					{{ translate("ui.only_one_active_battle") }}
				</div>
			</template>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppIconButton from "@/components/AppIconButton.vue";
import AppPrice from "@/components/AppPrice.vue";
import ColorRectangle from "@/components/FightSystem/ColorRectangle.vue";
import HorizontalDivider from "@/components/HorizontalDivider.vue";
import Tooltip from "@/components/Tooltip.vue";
import { FAMINE_HEALTH_LOSS_PER_TICK_PERCENTAGE } from "@/game/SiegeSystemRepository";
import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository.ts";
import useTileRepository from "@/hooks/useTileRepository";
import { usePopupStore } from "@/store/usePopupStore.ts";
import { type Reward, type TileCoordinates } from "@/types/tile.ts";
import { IconTypes, Placements } from "@/types/ui.ts";
import { formatPercent } from "@/utils/format";
import { resourceTypeToCurrencyType } from "@/utils/formatPrice.ts";
import { formatTimeString } from "@/utils/time";
import { translate } from "@/utils/translate.ts";
import { computed, markRaw } from "vue";

const props = defineProps<{
	coordinates: TileCoordinates;
}>();

const popupStore = markRaw(usePopupStore());

const ssr = markRaw(useSiegeSystemRepository());

const tileIndex = computed<number>((): number =>
	useTileRepository().getTileIndexByTileCoordinates(props.coordinates),
);

const popupStatus = computed<"inactive" | "active" | "famine">(
	(): "inactive" | "active" | "famine" => {
		if (ssr.mainEnemyTileIndex !== null) {
			if (tileIndex.value === ssr.mainEnemyTileIndex) {
				if (ssr.isFamineActive) return "famine";
				return "active";
			}
			// return "active-elsewhere";
			return "inactive";
		}
		return "inactive";
	},
);

const headerText = computed<string>((): string => {
	return translate("ui.enemy_territory");
});

const subheaderText = computed<string>((): string => {
	if (popupStatus.value === "inactive") {
		return translate("ui.available_battlefield");
	}
	if (popupStatus.value === "active") {
		return translate("ui.active_battlefield");
	}
	if (popupStatus.value === "famine") {
		return translate("ui.insufficient_supplies");
	}
	return translate("ui.available_battlefield");
});

const reports = computed<
	Array<{ text: string; value: number | string | null }>
>((): Array<{ text: string; value: number | string | null }> => {
	if (["active", "famine"].includes(popupStatus.value)) {
		const damages = ssr.getAllFriendlyDamage();
		return [
			{
				text: translate("ui.melee"),
				value: damages.melee,
			},
			{ text: translate("ui.ranged"), value: damages.ranged },
			{ text: translate("ui.siege"), value: damages.siege },
		];
	}
	return [
		{
			text: translate("ui.battle_duration"),
			value: formatTimeString(ssr.duration || 0),
		},
		{ text: translate("ui.damage_dealt"), value: ssr.damageDealt },
		{ text: translate("ui.damage_taken"), value: ssr.damageTaken },
		{
			text: translate("ui.units_defeated"),
			value: `${ssr.enemiesDefeated}/${ssr.enemiesTotal}`,
		},
	];
});

const rewards = computed<Reward | null>((): Reward | null =>
	tileIndex.value === ssr.mainEnemyTileIndex && ssr.rewards
		? ssr.rewards
		: ssr.getRewardsByTileIndex(tileIndex.value),
);

const close = (): void => {
	popupStore.hidePopup();
};
</script>

<style lang="scss" scoped>
.fight-tile-label-popup {
	gap: 0.5rem;
	border-width: 0.125rem;
	border-style: solid;
	border-image-source: linear-gradient(to top, #bd9032, #f0d386);
	border-image-slice: 1;
	width: 100%;
	height: 100%;
	@include flex-col-start;

	&--status {
		&-active {
			border-image-source: linear-gradient(
				to top,
				#daab2c,
				#f0d485,
				#daab2c
			);
		}
		&-inactive {
			border-image-source: linear-gradient(
				to top,
				#daab2c,
				#f0d485,
				#daab2c
			);
		}
		&-famine {
			border-image-source: linear-gradient(
				to top,
				#cf0032,
				#ff3d3d,
				#cf0032
			);
		}
		&-active-elsewhere {
			border-image-source: linear-gradient(
				to top,
				#daab2c,
				#f0d485,
				#daab2c
			);
		}
	}

	&__header {
		background-image: url($path-images + "backgrounds/fight-system/img-battlefield.avif");
		background-size: cover;
		width: 100%;
		height: 10.975rem;
		@include flex-col-end;

		&__text {
			position: absolute;
			top: 0.5rem;
			left: 0.75rem;
			color: rgb(244, 210, 120);
			font-weight: 800;
			font-size: 1.25rem;
			line-height: 1;
			text-shadow: 0.0875rem 0.0875rem 0.125rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}
	}

	&__subheader {
		padding-top: 0.5rem;
		width: 100%;
		color: rgb(255, 243, 221);
		font-weight: 900;
		font-weight: 900;
		font-size: 1rem;
		line-height: 1;
		text-shadow: 0.0875rem 0.0875rem 0.125rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
		&__header {
			color: rgb(255, 243, 221);
			font-weight: 800;
			font-size: 1rem;
			text-shadow: 0.0437rem 0.0437rem 0.0625rem rgba(0, 0, 0, 0.6);
		}
		&__message {
			border: solid 0.125rem rgb(207, 0, 50);
			background-color: rgb(85, 0, 20);
			width: 100%;
			height: 2rem;
			color: rgb(242, 220, 181);
			text-shadow: 0.0875rem 0.0875rem 0.125rem rgba(0, 0, 0, 0.6);
		}
		&__line {
			width: 100%;
			color: rgb(242, 220, 181);
			font-size: 0.9063rem;
			text-shadow: 0.0437rem 0.0437rem 0.0625rem rgba(0, 0, 0, 0.6);
			@include flex-row-between;
			padding: 0 1rem;
			&__value {
				color: #f4d278;
			}

			.after-dots {
				display: flex;
				flex: 1;
				&::before {
					flex: 1;
					margin: 0.25rem 0.25rem 0;
					border-bottom: 0.125rem dotted rgb(242, 220, 181);
					content: "";
				}
			}

			&--narrower {
				width: 100%;
				@include flex-row-between;
				padding: 0 1.5rem;

				&__unit {
					color: rgb(244, 210, 120);
					@include flex-col-end;
					position: relative;
					gap: 0.25rem;
					margin: 0.25rem 0;
					width: 6rem;
					height: 3rem;

					&__header {
						@include flex-row-center;
						position: absolute;
						top: 0;
						bottom: 2.25rem;
						align-items: flex-end;
						width: 120%;
						font-size: 0.75rem;
					}
				}
			}
		}
	}

	.horizontal-divider {
		width: 98%;
	}
	&__content {
		@include flex-col-center;
		gap: 1rem;
		padding-bottom: 0.75rem;
		width: 100%;
		line-height: 1;
		&__header {
			color: rgb(244, 210, 120);
			font-weight: 800;
			font-size: 1rem;
			text-shadow: 0.0875rem 0.0875rem 0.125rem rgba(0, 0, 0, 0.6);
		}
	}

	&--status {
		.fight-tile-label-popup__subheader {
			border-width: 0.1875rem;
			border-style: solid;
			border-image-source: linear-gradient(
				to right,
				rgb(244, 210, 120, 0),
				rgba(244, 210, 120, 0.8),
				rgba(244, 210, 120, 0.8),
				rgb(244, 210, 120, 0)
			);
			border-image-slice: 1;
			background-image: linear-gradient(
				to right,
				rgb(205, 142, 33, 0),
				rgba(205, 142, 33, 0.78),
				rgba(205, 142, 33, 0.8),
				rgb(205, 142, 33, 0)
			);
			width: 100%;
			height: 2.25rem;
			@include flex-row-center;
			padding-bottom: 0.125rem;
			padding-bottom: 0.75rem;
			color: rgb(255, 243, 221);
			text-shadow: 0.1313rem 0.1313rem 0.0625rem rgba(0, 0, 0, 0.6);
		}
	}

	.close-button {
		position: absolute;
		top: 0.5rem;
		right: 0.5rem;
	}

	&__rewards {
		border-width: 0.1875rem;
		border-style: solid;
		border-image-source: linear-gradient(
			to right,
			rgb(255, 232, 171, 0),
			rgba(255, 232, 171, 0.8),
			rgba(255, 232, 171, 0.8),
			rgb(255, 232, 171, 0)
		);
		border-image-slice: 1;
		background-image: linear-gradient(
			to right,
			rgb(242, 220, 181, 0),
			rgba(244, 210, 120, 0.78),
			rgba(244, 210, 120, 0.8),
			rgb(244, 210, 120, 0)
		);
		width: 100%;
		height: 2.25rem;
		@include flex-row-center;
		padding-bottom: 0.25rem;
		color: rgb(55, 38, 21);
		font-weight: 800;
		font-size: 1.5rem;

		:deep(.price span) {
			color: rgb(55, 38, 21);
			text-shadow: none;
		}
	}

	&__description {
		color: rgb(255, 243, 221);
		text-shadow: 0.0875rem 0.0875rem 0.125rem rgba(0, 0, 0, 0.6);
	}

	&__message {
		border-width: 0.1875rem;
		border-style: solid;
		border-image-source: linear-gradient(
			to right,
			rgb(255, 61, 61, 0),
			rgba(255, 61, 61, 0.8),
			rgba(255, 61, 61, 0.8),
			rgb(255, 61, 61, 0)
		);
		border-image-slice: 1;
		background-image: linear-gradient(
			to right,
			rgb(207, 0, 50, 0),
			rgba(207, 0, 50, 0.78),
			rgba(207, 0, 50, 0.8),
			rgb(207, 0, 50, 0)
		);
		width: 100%;
		height: 2.25rem;
		@include flex-row-center;
		padding-bottom: 0.125rem;
		color: rgb(255, 243, 221);
		text-shadow: 0.1313rem 0.1313rem 0.0625rem rgba(0, 0, 0, 0.6);
	}
}
</style>
